import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useCustomColors } from "../../hooks/useCustomColors";
import AppDialog from "../lotto/AppDialog";
import AuthField from "../lotto/AuthField";

const FAKE_LOGIN_TOAST_ID = "game-fake-login-error";

interface FakeGameSignInDialogProps {
  open: boolean;
  gameName: string;
  onClose: () => void;
}

interface FakeLoginValues {
  username: string;
  password: string;
}

const FakeGameSignInDialog = ({
  open,
  gameName,
  onClose,
}: FakeGameSignInDialogProps) => {
  const colors = useCustomColors();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const schema = useMemo(
    () =>
      Yup.object({
        username: Yup.string().trim().required(t("validation_username_required")),
        password: Yup.string().required(t("validation_password_required")),
      }).required(),
    [t]
  );
  // The parent clears gameName on close; keep the last one through the fade-out.
  const [shownName, setShownName] = useState(gameName);
  if (gameName && gameName !== shownName) setShownName(gameName);
  const {
    control,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors },
  } = useForm<FakeLoginValues>({
    resolver: yupResolver(schema),
    defaultValues: { username: "", password: "" },
  });

  useEffect(() => {
    if (open) setFocus("username");
  }, [open, setFocus]);

  const close = () => {
    reset();
    setShowPassword(false);
    onClose();
  };

  const submit = () => {
    if (toastVisible || toast.isActive(FAKE_LOGIN_TOAST_ID)) return;

    setToastVisible(true);
    toast.error(t("game_login_error"), {
      toastId: FAKE_LOGIN_TOAST_ID,
      onClose: () => setToastVisible(false),
    });
  };

  return (
    <AppDialog
      open={open}
      onClose={close}
      title={t("game_login_title", { game: shownName })}
      subtitle={t("game_login_subtitle")}
      width={470}
    >
      <Box
        component="form"
        data-testid="fake-game-login-form"
        noValidate
        onSubmit={handleSubmit(submit)}
      >
        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <AuthField
              {...field}
              label={t("sign_up_username")}
              icon={PersonRoundedIcon}
              autoComplete="off"
              autoFocus
              placeholder={t("sign_up_username_placeholder")}
              inputProps={{ "aria-label": t("sign_up_username") }}
              error={!!errors.username}
              helperText={errors.username?.message}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <AuthField
              {...field}
              label={t("login_password")}
              icon={LockRoundedIcon}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder={t("sign_up_password_placeholder")}
              inputProps={{ "aria-label": t("login_password") }}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      edge="end"
                      aria-label={
                        showPassword
                          ? t("game_login_hide_password")
                          : t("game_login_show_password")
                      }
                      onClick={() => setShowPassword((visible) => !visible)}
                      sx={{ color: colors.text.secondary }}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Button
          type="submit"
          fullWidth
          disabled={toastVisible}
          sx={{ mt: "8px", height: "52px", borderRadius: "14px" }}
        >
          {t("login")}
        </Button>
        <Typography
          sx={{
            mt: "18px",
            textAlign: "center",
            fontSize: "13px",
            lineHeight: 1.6,
            color: colors.text.secondary,
          }}
        >
          {t("game_login_contact")}
        </Typography>
      </Box>
    </AppDialog>
  );
};

export default FakeGameSignInDialog;
